import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const PAGE_TITLES = {
  '/': 'Dashboard',
  '/patients': 'Patients',
  '/cases': 'Cases',
  '/photos': 'Photos',
  '/2d-checks': '2D Checks',
  '/maxillary-incisal-edge': 'Maxillary Incisal Edge',
  '/users': 'User Management',
  '/audit': 'Audit Logs',
};

const getPageTitle = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.includes('/smile-design')) return 'Smile Designer';
  if (pathname.includes('/exam')) return 'Exam';
  if (pathname.includes('/compare')) return 'Photo Comparison';
  if (pathname.includes('/photos')) return 'Photos';
  if (pathname.includes('/cases')) return 'Cases';
  if (pathname.includes('/patients')) return 'Patients';
  return 'SmileWizard';
};

const Navbar = ({ onMenuToggle }) => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const initials = userProfile?.displayName
    ? userProfile.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : (currentUser?.email?.[0] || 'U').toUpperCase();

  return (
    <nav className="bg-blue-700 shadow-md sticky top-0 z-10">
      <div className="px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Left: Logo + Branding */}
          <div className="flex items-center">
            <button
              onClick={onMenuToggle}
              className="lg:hidden p-2 -ml-1 mr-2 rounded-md text-blue-200 hover:text-white hover:bg-blue-600"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex-shrink-0 flex items-center">
              {/* Tooth icon */}
              <div className="h-8 w-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C9.5 2 7.5 3.5 7 5.5C6.5 7.5 5 8 4 9.5C3 11 3 13 4 14.5C5 16 5.5 18 6 20C6.5 22 8 22 9 20C10 18 11 17 12 17C13 17 14 18 15 20C16 22 17.5 22 18 20C18.5 18 19 16 20 14.5C21 13 21 11 20 9.5C19 8 17.5 7.5 17 5.5C16.5 3.5 14.5 2 12 2Z" />
                </svg>
              </div>
              <span className="ml-2 text-lg sm:text-xl font-bold text-white tracking-tight">SmileWizard</span>
            </div>
          </div>

          {/* Center: Page Title */}
          <div className="hidden sm:block absolute left-1/2 transform -translate-x-1/2">
            <h1 className="text-white text-base font-medium">{pageTitle}</h1>
          </div>

          {/* Right: User dropdown */}
          <div className="flex items-center">
            {currentUser && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center space-x-2 text-white hover:bg-blue-600 rounded-lg px-2 py-1.5 transition"
                >
                  <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold text-white">
                    {initials}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">
                    {userProfile?.displayName || currentUser.email}
                  </span>
                  <svg className="hidden sm:block w-4 h-4 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{userProfile?.displayName || 'User'}</p>
                      <p className="text-xs text-gray-500">{currentUser.email}</p>
                      <span className="inline-block mt-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                        {userProfile?.role || 'User'}
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
